import { type Box3, Matrix4 } from 'three'
import type {
  SpatialNode,
  TileHierarchyState,
  TileResourceKind,
  TilesetDocument,
} from '@/plugins/tiles/contracts'
import {
  getMaximumStretch,
  getTilesBoundingBox,
  validateTilesTransform,
} from '@/plugins/tiles/spatial/tilesSpatial'
import { TilesError } from '@/plugins/tiles/TilesError'
import type { TilesRuntimeOptions } from '@/plugins/tiles/types'

/** Record 自有可变节点；description 永不写入分类、请求或驻留状态。 */
export interface RuntimeTileNode {
  readonly id: string
  readonly description: SpatialNode
  readonly parent?: RuntimeTileNode
  readonly depth: number
  readonly tileMatrix: Matrix4
  readonly sourceToRecord: Matrix4
  readonly bounds: Box3
  readonly traversalBounds: Box3
  readonly effectiveError: number
  readonly documentChain: readonly string[]
  children: RuntimeTileNode[]
  contentKind: TileResourceKind
  hierarchyState: TileHierarchyState
  hasUnknownDescendants: boolean
}

type HierarchyOptions = Pick<TilesRuntimeOptions, 'maxHierarchyDepth' | 'maxMetadataBytes'>

function canonicalDocument(url: string): string {
  const parsed = new URL(url)
  parsed.hash = ''
  return parsed.href
}

/** 只维护已解析树和派生空间，不启动网络，也不持有探测响应或内容包。 */
export class ExplicitHierarchyProvider {
  readonly root: RuntimeTileNode
  readonly sourceToRecord: Matrix4
  private readonly _nodes = new Map<string, RuntimeTileNode>()
  private _options: HierarchyOptions
  private _generation = 0
  private _metadataBytes = 0

  constructor(document: TilesetDocument, sourceToRecord: Matrix4, options: HierarchyOptions) {
    validateTilesTransform(sourceToRecord)
    this.sourceToRecord = sourceToRecord.clone()
    this._options = { ...options }
    this.checkBudget(document)
    const staged = new Map<string, RuntimeTileNode>()
    this.root = this.build(
      document.root,
      undefined,
      undefined,
      [canonicalDocument(document.url)],
      staged
    )
    this.commitNodes(staged)
    this._metadataBytes = document.byteLength
  }

  get generation(): number {
    return this._generation
  }

  /** 新额度只约束后续安装；不会丢弃已有层次或重写来源描述。 */
  configure(options: HierarchyOptions): void {
    this._options = {
      maxHierarchyDepth: options.maxHierarchyDepth,
      maxMetadataBytes: options.maxMetadataBytes,
    }
  }

  get metadataBytes(): number {
    return this._metadataBytes
  }

  getNode(id: string): RuntimeTileNode | undefined {
    return this._nodes.get(id)
  }

  /** 使迟到安装失效；已提交描述和分类保留，便于隐藏后重挂。 */
  invalidate(): void {
    this._generation++
    for (const node of this._nodes.values()) {
      if (node.hierarchyState === 'pending') node.hierarchyState = 'unknown'
    }
  }

  setClassification(
    node: RuntimeTileNode,
    kind: TileResourceKind,
    generation = this._generation
  ): boolean {
    if (generation !== this._generation) return false
    this.assertOwned(node)
    if (!node.description.content) this.fail(node, '无内容节点不能分类')
    if (kind === 'tileset') this.assertExternal(node)
    if (node.contentKind !== 'unknown' && node.contentKind !== kind) {
      this.fail(node, '已确定的内容类型不能被重新分类')
    }
    if (node.contentKind === kind) return true
    node.contentKind = kind
    node.hierarchyState = kind === 'mesh' || kind === 'unsupported' ? 'ready' : 'unknown'
    this.refreshAncestors(node)
    return true
  }

  setHierarchyState(
    node: RuntimeTileNode,
    state: 'unknown' | 'pending' | 'failed',
    generation = this._generation
  ): boolean {
    if (generation !== this._generation) return false
    this.assertOwned(node)
    if (
      node.contentKind === 'mesh' ||
      node.contentKind === 'unsupported' ||
      !node.description.content
    ) {
      this.fail(node, '仅未知内容或外部层次可更新读取状态')
    }
    if (node.contentKind === 'tileset' && node.hierarchyState === 'ready') {
      this.fail(node, '已安装外部层次不能回退到读取状态')
    }
    node.hierarchyState = state
    this.refreshAncestors(node)
    return true
  }

  /** 先完整构造并校验，再一次提交；取消和世代失效返回 false，不触碰已有树。 */
  installExternal(
    node: RuntimeTileNode,
    document: TilesetDocument,
    generation = this._generation,
    signal?: AbortSignal
  ): boolean {
    if (generation !== this._generation || signal?.aborted) return false
    this.assertOwned(node)
    this.assertExternal(node)
    if (node.contentKind !== 'unknown' && node.contentKind !== 'tileset') {
      this.fail(node, '不能为非外部内容安装层次')
    }
    if (node.hierarchyState === 'ready') return false
    const url = canonicalDocument(document.url)
    if (node.documentChain.includes(url)) {
      throw new TilesError({
        code: 'hierarchy-cycle',
        stage: 'hierarchy',
        sourceUrl: document.url,
        tileId: node.id,
        message: '外部 tileset 引用形成祖先文档环',
      })
    }
    this.checkBudget(document)
    const staged = new Map<string, RuntimeTileNode>()
    const child = this.build(
      document.root,
      node,
      `${node.id}/external`,
      [...node.documentChain, url],
      staged
    )
    if (generation !== this._generation || signal?.aborted) return false
    this.commitNodes(staged)
    node.children = [child]
    node.contentKind = 'tileset'
    node.hierarchyState = 'ready'
    this._metadataBytes += document.byteLength
    this.refreshAncestors(node)
    return true
  }

  private build(
    description: SpatialNode,
    parent: RuntimeTileNode | undefined,
    prefix: string | undefined,
    documentChain: readonly string[],
    staged: Map<string, RuntimeTileNode>
  ): RuntimeTileNode {
    const depth = parent ? parent.depth + 1 : 0
    if (depth > this._options.maxHierarchyDepth) {
      throw new TilesError({
        code: 'limit-exceeded',
        stage: 'hierarchy',
        sourceUrl: description.documentUrl,
        tileId: description.id,
        message: '组合显式与外部层次超过最大深度',
      })
    }
    const tileMatrix = (parent?.tileMatrix.clone() ?? new Matrix4()).multiply(
      new Matrix4().fromArray(description.transform)
    )
    const bounds = getTilesBoundingBox(description.boundingVolume, tileMatrix, this.sourceToRecord)
    const node: RuntimeTileNode = {
      id: prefix ? `${prefix}#${description.path}` : description.id,
      description,
      parent,
      depth,
      tileMatrix,
      sourceToRecord: this.sourceToRecord,
      bounds,
      traversalBounds: bounds.clone(),
      effectiveError:
        description.error.value *
        getMaximumStretch(tileMatrix) *
        getMaximumStretch(this.sourceToRecord),
      documentChain,
      children: [],
      contentKind: 'unknown',
      hierarchyState: description.content ? 'unknown' : 'ready',
      hasUnknownDescendants: !!description.content,
    }
    if (this._nodes.has(node.id) || staged.has(node.id)) this.fail(node, '运行时节点实例 ID 冲突')
    staged.set(node.id, node)
    node.children = description.children.map((child) =>
      this.build(child, node, prefix, documentChain, staged)
    )
    this.refresh(node)
    return node
  }

  private refresh(node: RuntimeTileNode): void {
    node.traversalBounds.copy(node.bounds)
    node.hasUnknownDescendants =
      !!node.description.content &&
      (node.contentKind === 'unknown' ||
        (node.contentKind === 'tileset' && node.hierarchyState !== 'ready'))
    for (const child of node.children) {
      node.traversalBounds.union(child.traversalBounds)
      node.hasUnknownDescendants ||= child.hasUnknownDescendants
    }
  }

  private refreshAncestors(node: RuntimeTileNode): void {
    let current: RuntimeTileNode | undefined = node
    while (current) {
      this.refresh(current)
      current = current.parent
    }
  }

  private assertOwned(node: RuntimeTileNode): void {
    if (this._nodes.get(node.id) !== node) this.fail(node, '节点不属于当前层次实例')
  }

  private assertExternal(node: RuntimeTileNode): void {
    if (
      !node.description.content ||
      node.description.hasDeclaredChildren ||
      node.description.children.length
    ) {
      this.fail(node, '外部引用 tile 必须有 content 且不能声明 children')
    }
  }

  private checkBudget(document: TilesetDocument): void {
    if (
      !Number.isFinite(document.byteLength) ||
      document.byteLength < 0 ||
      this._metadataBytes + document.byteLength > this._options.maxMetadataBytes
    ) {
      throw new TilesError({
        code: 'limit-exceeded',
        stage: 'hierarchy',
        sourceUrl: document.url,
        message: '层次描述超过元数据预算或字节计数无效',
      })
    }
  }

  private commitNodes(staged: Map<string, RuntimeTileNode>): void {
    for (const [id, node] of staged) this._nodes.set(id, node)
  }

  private fail(node: RuntimeTileNode, message: string): never {
    throw new TilesError({
      code: 'invalid-input',
      stage: 'hierarchy',
      sourceUrl: node.description.documentUrl,
      tileId: node.id,
      contentId: node.description.content?.id,
      message,
    })
  }
}
